'use client';

import styled, { css } from 'styled-components';
import { useOffersContext } from '@/app/components/hotel-detail/OffersProvider';
import { useIsMobile } from '@/app/components/hotel-detail/hooks/useMediaSSR';
import { trackHotelReviewsLink, trackSelectRoomTypeClick } from '@/app/components/hotel-detail/trackInteractionEvents';
import { DealBadge } from '@/app/components/hotel-detail/DealBadge';
import customCss from '@/app/components/hotel-detail/css/customCss';
import { useTranslation } from '@/app/components/translations/Translation';
import useSelectedTransport from '@/app/components/hotel-detail/hooks/useSelectedTransport';
import { ReviewsSummary } from '@/app/components/hotel-detail/ReviewsSummary';
import { useCuriosity } from '@/app/components/hotel-detail/CuriosityProvider';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import { isTripAdvisorSource, isTrustYouSource } from '@/app/common/reviews/reviewSource';
import Stars from '@/app/common/components/Stars';
import { useCommonClient } from '@/app/common/commonClientProvider';
import { URLParameters } from '@/app/common/urlParameters';
import { useInitialParams } from '@/app/components/hotel-detail/InitialParamsProvider';
import getSearchParams from '@/app/components/search/common/getSearchParams';
import { createDetailPageUserActionPayload } from '@/app/common/starTrack/events/detailPageUserAction.ts';
import { DebugReviewsInfo } from './DebugReviewsInfo';
import { HeroAddress } from './HeroAddress';
import ShareButton from './ShareButton';
import { useFetchHotelStatic } from './hooks/useFetchHotelStatic';
import { GoToRoomsButton } from './GoToRoomsButton';
import { useMetaReview } from './hooks/useMetaReview';

type InfoSectionProps = React.HTMLAttributes<HTMLElement>;

export const HeroHotelInformationInfoContainer = styled.div`
  background-color: ${({ theme }) => theme.kallisto.color.background.static.backgroundLayer02};
  position: relative;
  display: flex;
  flex-direction: column;
  grid-area: hotel-info;
  padding: 16px;

  @media ${customCss.breakpoints.sm} {
    background-color: inherit;
    padding: 16px 24px;
  }

  @media ${customCss.breakpoints.lg} {
    padding: 16px 24px;
  }

  @media ${customCss.breakpoints.lgPlus} {
    padding: 16px 40px;
  }
  @media ${customCss.breakpoints.xl} {
    padding: 16px 0;
  }
`;

const TitleContainer = styled.div`
  display: flex;
`;

const Title = styled.h1`
  margin: 0;

  ${({ theme }) => theme.kallisto.mixins.font('Title2')};
  font-size: 24px;
  line-height: 30px;

  @media ${customCss.breakpoints.sm} {
    font-size: 28px;
    line-height: 40px;
  }

  @media ${customCss.breakpoints.lg} {
    ${({ theme }) => theme.kallisto.mixins.font('Title1')};
  }
`;

const RatingContainer = styled.div`
  display: flex;
  align-items: center;
  grid-area: rating;
`;

const Info = styled.div<{ $rating: number }>`
  margin-top: 0.25rem;
  display: grid;
  align-items: center;
  grid-column-gap: 8px;
  grid-template-columns: auto;
  grid-template-rows: repeat(3, auto);
  grid-template-areas:
    'rating'
    'address'
    'reviews'
    'guest-reviews';

  .info-book,
  .info-share {
    display: none;
  }

  ${RatingContainer} {
    padding: 0 0 16px;
  }

  .info-reviews {
    padding: 16px 0;
  }

  .info-reviews,
  ${RatingContainer} {
    border-bottom: 1px solid ${(p) => p.theme.kallisto.color.border.static.borderDisabled};
  }

  @media ${customCss.breakpoints.sm} {
    grid-row-gap: 8px;
    grid-template-columns: auto 1fr auto;
    grid-template-rows: auto auto;
    grid-template-areas:
      '${(p) => (p.$rating > 0 ? 'rating' : 'address')} address share'
      'reviews reviews share';

    .info-share {
      display: initial;
    }

    ${RatingContainer} {
      padding: 0 8px 0 0;
      border-right: 1px solid ${(p) => p.theme.kallisto.color.border.static.borderDisabled};
    }

    .info-reviews {
      padding: 0;
    }

    .info-reviews,
    ${RatingContainer} {
      border-bottom: 0;
    }
  }

  @media ${customCss.breakpoints.xl} {
    grid-template-columns: auto 1fr auto auto;
    grid-template-rows: auto;
    grid-template-areas: '${(p) => (p.$rating > 0 ? 'rating' : 'address')} address share book';

    .info-reviews {
      display: none;
    }

    .info-book {
      display: initial;
    }
  }
`;

export default function HeroHotelInformation(props: InfoSectionProps) {
  const { offersData } = useOffersContext();
  const { sendCuriosityClickEvent } = useCuriosity();
  const { commonData, localization } = usePageWrapperData();
  const { data: staticData } = useFetchHotelStatic();
  const { reviewsDisabled } = localization.configValuesApp;
  const isTripAdvisor = isTripAdvisorSource(staticData?.hotelOpinion.reviewSource);
  // Only fetch the TrustYou meta-review for an explicit TrustYou hotel (not TA/unknown/absent/Expedia).
  const isTrustYou = isTrustYouSource(staticData?.hotelOpinion.reviewSource);
  const { metaReview } = useMetaReview(
    isTrustYou && !reviewsDisabled ? staticData?.hotelOpinion.opinionId : undefined,
    localization.lang
  );
  const { starTrack } = useCommonClient();
  const searchParams = getSearchParams();
  const searchId = searchParams[URLParameters.SEARCH_ID];
  const initialParams = useInitialParams();
  const { productDetailKey } = initialParams;

  const isMobile = useIsMobile(commonData.isMobileDevice);

  const { data: transportData } = useSelectedTransport();
  const transportIsSoldOut = transportData?.transports.length === 0;

  const reviewsSummaryClick = () => {
    starTrack?.detailPageUserAction(
      createDetailPageUserActionPayload({
        searchId: searchId || '',
        productDetailKey,
        action: 'REVIEW',
      })
    );
    if (isMobile) {
      sendCuriosityClickEvent({
        event_name: 'click_reviews_link',
        event_context: 'review_summary',
      });
    } else {
      sendCuriosityClickEvent({
        event_name: 'click_reviews_show_all',
        event_context: 'review_summary',
      });
    }

    trackHotelReviewsLink();
  };

  const reviewCount = isTripAdvisor
    ? staticData?.hotelOpinion.numberOfReviews
    : metaReview?.reviewCount || staticData?.hotelOpinion.numberOfReviews;
  const reviewScore = isTripAdvisor
    ? staticData?.hotelOpinion.rating
    : metaReview?.score || staticData?.hotelOpinion.rating;
  // TA needs a rating, TY needs an opinionId; any other (unknown/absent/Expedia) source → no reviews.
  const hasReviewData = isTripAdvisor
    ? !!staticData?.hotelOpinion.rating
    : isTrustYou && !!staticData?.hotelOpinion.opinionId;

  return (
    <HeroHotelInformationInfoContainer {...props}>
      <TitleContainer>
        <DealBadge
          size={isMobile ? 'small' : 'default'}
          css={css`
            margin-bottom: 0.25rem;

            @media ${customCss.breakpoints.sm} {
              margin-bottom: 0.5rem;
            }
          `}
        />
        <Title>{staticData?.name}</Title>
      </TitleContainer>
      <Info $rating={staticData?.rating ?? 0}>
        {!!staticData?.rating && (
          <RatingContainer>
            <Stars data-testid="Rating" stars={staticData?.rating || 0} black={true} />
          </RatingContainer>
        )}

        {!reviewsDisabled && (
          <ReviewsSummary
            reviewsScore={reviewScore}
            reviews={reviewCount}
            reviewSource={staticData?.hotelOpinion.reviewSource}
            onClick={reviewsSummaryClick}
            className="info-reviews"
            showMore={hasReviewData ? (isMobile ? 'dialog' : 'scroll') : 'none'}
            trustYouId={isTripAdvisor ? undefined : staticData?.hotelOpinion.opinionId}
            style={{ gridArea: 'reviews' }}
            css={css`
              font-size: ${(p) => p.theme.kallisto.font.size['175']};
            `}
          />
        )}

        {!isMobile && staticData?.direction ? <HeroAddress direction={staticData.direction} /> : undefined}

        {!isMobile && !initialParams.isHotelStaticPage ? (
          <ShareButton
            hotelName={staticData?.name ?? ''}
            className="info-share"
            style={{ gridArea: 'share' }}
            css={`
              & svg,
              & [data-library-icon] {
                width: 20px;
                height: 20px;
              }
            `}
          />
        ) : null}

        {offersData?.noAvailableRooms !== true && !transportIsSoldOut && !initialParams.isHotelStaticPage ? (
          <BookCta
            // price={offersData?.cheapestFilteredMealPlan?.finalPrice}
            // roomsIncludedInPriceForCheapestRoom={offersData?.roomsIncludedInPriceForCheapestRoom}
            style={{ gridArea: 'book' }}
            className="info-book"
          />
        ) : null}
      </Info>
      <DebugReviewsInfo />
    </HeroHotelInformationInfoContainer>
  );
}

function BookCta(props: React.HTMLAttributes<HTMLElement>) {
  const { t } = useTranslation();
  const { data: hotelStaticData } = useFetchHotelStatic();
  const { sendCuriosityClickEvent } = useCuriosity();

  const getCTALabel = (currentAccommodationType: AccommodationType | undefined) =>
    currentAccommodationType === 'HOTELS'
      ? t('vacation.select.navigation.cta')
      : t('vacation.select.navigation.cta.accommodation');

  const onCtaClick = () => {
    trackSelectRoomTypeClick();
    sendCuriosityClickEvent({
      event_name: 'click_cta_select_room_type',
      event_context: 'hero',
    });
  };

  return (
    <GoToRoomsButton
      {...props}
      onClick={onCtaClick}
      css={css`
        white-space: pre-wrap;
      `}
    >
      {getCTALabel(hotelStaticData?.accommodationType)}
    </GoToRoomsButton>
  );
}
